import { TodoRow } from "@/services/api/fetchers/todos";
import { TodoDatePeriod } from "../types";

/**
 * Calculate a fractional position between two positions using the midpoint.
 * This avoids needing to update all sibling todos when inserting.
 */
export function calculateFractionalPosition(beforePosition: number | null, afterPosition: number | null): number {
  if (beforePosition === null && afterPosition === null) {
    // No items yet, start at 1
    return 1;
  }

  if (beforePosition === null) {
    // Inserting at the beginning
    if (afterPosition! <= 0) {
      // If first item is at 0 or negative, go more negative
      return afterPosition! - 1;
    }
    return afterPosition! / 2;
  }

  if (afterPosition === null) {
    // Inserting at the end
    return beforePosition + 1;
  }

  // Insert between two positions
  return (beforePosition + afterPosition) / 2;
}

/**
 * Calculate the position update for a dragged todo item.
 * Returns the id and new position for the moved item, or null if no update needed.
 */
export function calculateTodoReorderUpdate(
  originalTodos: TodoRow[],
  reorderedTodos: { id: number | string }[]
): { id: number; position: number } | null {
  // Find the item with the largest index change - this is the dragged item
  let movedItemId: number | null = null;
  let newIndex = -1;
  let maxIndexChange = 0;

  for (let i = 0; i < reorderedTodos.length; i++) {
    const itemId = reorderedTodos[i].id;
    const oldIndex = originalTodos.findIndex((t) => t.id === itemId);
    const indexChange = Math.abs(oldIndex - i);

    if (indexChange > maxIndexChange) {
      maxIndexChange = indexChange;
      movedItemId = typeof itemId === "number" ? itemId : null;
      newIndex = i;
    }
  }

  if (movedItemId === null || newIndex === -1 || maxIndexChange === 0) {
    return null;
  }

  const todo = originalTodos.find((t) => t.id === movedItemId);
  if (!todo) return null;

  // Calculate new position between neighbors in the reordered list
  const prevItem = newIndex > 0 ? reorderedTodos[newIndex - 1] : null;
  const nextItem = newIndex < reorderedTodos.length - 1 ? reorderedTodos[newIndex + 1] : null;

  const prevTodo = prevItem ? originalTodos.find((t) => t.id === prevItem.id) : null;
  const nextTodo = nextItem ? originalTodos.find((t) => t.id === nextItem.id) : null;

  const newPosition = calculateFractionalPosition(prevTodo?.position ?? null, nextTodo?.position ?? null);

  // Only return update if the position actually changes
  if (newPosition === todo.position) {
    return null;
  }

  return { id: movedItemId, position: newPosition };
}

export function getFilteredTodos(todos: TodoRow[], filter: TodoDatePeriod): TodoRow[] {
  // If showing done todos, filter only for completed items, sorted by most recent first
  if (filter === "done") {
    return todos
      .filter((todo) => todo.done)
      .sort((a, b) =>
        b.done_at && a.done_at ? new Date(b.done_at).getTime() - new Date(a.done_at).getTime() : b.position - a.position
      );
  }

  let filtered = todos;

  if (filter !== "upcoming") {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const weekStart = new Date(today);
    const dayOfWeek = weekStart.getDay();
    const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Sunday=0, Monday=1
    weekStart.setDate(weekStart.getDate() + daysToMonday);

    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);

    // Collect overdue todos to always include them
    const overdueTodos = todos.filter((todo) => {
      if (!todo.due_date) return false;

      const dueDate = new Date(todo.due_date);
      dueDate.setHours(0, 0, 0, 0);

      return dueDate.getTime() < today.getTime();
    });

    filtered = todos.filter((todo) => {
      if (!todo.due_date) return false;

      const dueDate = new Date(todo.due_date);
      dueDate.setHours(0, 0, 0, 0);

      if (filter === "today") {
        return dueDate.getTime() === today.getTime();
      }

      if (filter === "tomorrow") {
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        return dueDate.getTime() === tomorrow.getTime();
      }

      if (filter === "week") {
        return dueDate.getTime() >= weekStart.getTime() && dueDate.getTime() <= weekEnd.getTime();
      }

      return true;
    });

    // Combine overdue todos with filtered results, removing duplicates
    const combinedIds = new Set(overdueTodos.map((t) => t.id));
    filtered = [...overdueTodos, ...filtered.filter((t) => !combinedIds.has(t.id))];
  }

  // Filter out done todos except those completed today, and sort by position to maintain order
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todayEnd = new Date(todayStart);
  todayEnd.setDate(todayEnd.getDate() + 1);

  return filtered
    .filter((todo) => {
      // Keep all non-completed todos
      if (!todo.done) return true;

      // Keep completed todos from today
      if (todo.done_at) {
        const doneDate = new Date(todo.done_at);
        return doneDate >= todayStart && doneDate < todayEnd;
      }

      return false;
    })
    .sort((a, b) => a.position - b.position);
}
